import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ThemeBuilderService } from '../../theme/services/theme-builder.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';

import { UITheme, UIThemeVariable } from '../theme.interface';

/**
 * Component allowing UI theme building and management.
 */
@Component({
	selector: 'theme-builder',
	templateUrl: './theme-builder.component.html',
	styleUrl: './theme-builder.component.css'
})
export class ThemeBuilderComponent implements OnInit, OnDestroy {
	private userServiceSubscription!: Subscription;

	private PREVIEW_STYLE_ID = 'theme-preview-vars';

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	theme!: UITheme;
	themes: UITheme[] = [];
	themeReady: boolean = false;
	currentThemeName: string = '';

	constraints: any = {};
	pagination = {
		page: 1,
		elementsPerPage: 50,
		sorting: {
			sort: 'created,desc',
		}
	};

	inprogress: boolean = false;

	constructor(
		public themeBuilderService: ThemeBuilderService,
		private userService: UserService,
		private alertsService: AlertsService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} else if ("userPrefsUpdate" === eventId) {
				this.userPermissions = this.userService.getUserPermissionsMap();
			}
		});

		this.currentThemeName = this.getTheme();
		this.theme = this.buildNewTheme();
		this.themeBuilderService.disableThemes();
		this.loadThemes();
	}

	ngOnDestroy() {
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
	}

	/**
	 * Theme builder factory.
	 */
	private buildNewTheme(): UITheme {
		let newTheme: UITheme = {
			id: undefined,
			domain: undefined,
			name: '',
			label: '',
			created: undefined,
			modified: undefined,
			enabled: true,
			variables: [
				{ label: 'Background', name: '--color-background', value: '#f0f0f0', type: 'color' },
				{ label: 'Primary', name: '--color-primary', value: '#ffffff', type: 'color' },
				{ label: 'Secondary', name: '--color-secondary', value: '#f0f0f0', type: 'color' },
				{ label: 'Tertiary', name: '--color-tertiary', value: '#ffffff', type: 'color' },
				{ label: 'Accent', name: '--color-accent', value: '#229185', type: 'color' },
				{ label: 'Primary Button', name: '--color-primary-button', value: '#23527b', type: 'color' },
				{ label: 'Primary Button Text', name: '--color-primary-button-text', value: '#ffffff', type: 'color' },
				{ label: 'Secondary Button', name: '--color-secondary-button', value: '#3e7ab6', type: 'color' },
				{ label: 'Secondary Button Text', name: '--color-secondary-button-text', value: '#ffffff', type: 'color' },
				{ label: 'Link', name: '--color-link-text', value: '#347ab7', type: 'color' },
				{ label: 'Main Layout Text', name: '--color-primary-layout-text', value: '#545454', type: 'color' },
				{ label: 'Main Layout Icon', name: '--color-primary-layout-icon', value: '#737373', type: 'color' },
				{ label: 'Primary Text', name: '--color-primary-text', value: '#141415', type: 'color' },

				{ label: 'Primary', name: '--font-primary', value: 'sans-serif', type: 'font' },
				{ label: 'Primary Weight', name: '--font-weight-base', value: '400', type: 'font' },
				{ label: 'Main Header Weight', name: '--font-weight-navigation', value: '500', type: 'font' },
				
//				{ label: 'Main Header Text', name: '--main-header-copy', value: '', type: 'copy' },
				{ label: 'Footer Text', name: '--footer-copy', value: '', type: 'copy' },
				
//				Do we want to expose logo? Would require image upload and possibly base64 storage.
//				{ label: 'Header', name: '--logo-navbar-url', value: '', type: 'logo' },
//				{ label: 'Home', name: '--logo-login-modal-url', value: '', type: 'logo' },
//				{ label: 'Login', name: '--logo-home-page-brand-url', value: '', type: 'logo' }
			]
		};
		return newTheme;
	}

	/**
	 * Loads the themes using the current constraints.
	 */
	loadThemes(page?: number): void {
		this.inprogress = true;
		this.themeReady = false;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.themeBuilderService.getThemes(index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data?.content?.length) {
					this.themes = [];
				} else {
					this.themes = data.content;
					this.theme = data.content[0];
					this.loadTheme();

					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
				}
				this.inprogress = false;
			},
			(err: any) => {
				console.error("Failed to fetch themes.", err);
				this.themes = [];
				this.inprogress = false;
			}
		);
	}

	loadTheme(): void {
		console.log(this.theme);

		if (!this.theme?.variables) {
			console.warn('No theme variables found.');
			return;
		}

		this.clearPreviewVariables();
		this.setTheme(this.theme.name);
		this.applyPreviewVariables(this.theme.variables);

		this.themeReady = true;
	}

	newTheme(): void {
		// Reset the displayed theme to the default.
		// New themes are based on the default 'Light' theme.
		this.setTheme('');

		this.theme = this.buildNewTheme();
		this.loadTheme();
	}

	/**
	 * Saves the theme.
	 */
	save(): void {
		console.log(this.theme);

		if (this.theme['id']) {
			this.themeBuilderService.updateTheme(this.theme['id'], this.theme).then(
				(response: any) => {
					this.alertsService.success({
						message: "Theme updated successfully."
					});
					
					this.themeBuilderService.applyThemes();
					this.close();
				},
				(err: any) => {
					console.error("Failed to update theme.", err);
					this.alertsService.error({
						message: "Failed to update the theme. Please try again."
					});
				}
			);
		} else {
			this.themeBuilderService.createTheme(this.theme).then(
				(response: any) => {
					this.alertsService.success({
						message: "Theme created successfully."
					});
					
					this.themeBuilderService.applyThemes();
					this.close();
				},
				(err: any) => {
					console.error("Failed to add theme.", err);
					let message = "Failed to create the theme. Please try again.";
					if (err.status >= 400 && err.status < 500) {
						message = `Failed to create the theme. ${err.error}`;
					}
					this.alertsService.error({
						message: message
					});
				}
			);
		}
	}

	getVariable(name: string): string {
		const variable = this.theme?.variables?.find((v: any) => v.name === name);
		return variable?.value ?? '';
	}

	private setVariable(variable: UIThemeVariable, value: string): void {
		if (!this.theme.variables) {
			this.theme.variables = [];
		}

		let existingVariable = this.theme.variables.find((v: UIThemeVariable) => v.name === variable.name);

		if (existingVariable) {
			variable.value = value;
		} else {
			this.theme.variables.push(variable);
		}
	}

	commitVariable(variable: UIThemeVariable, event: Event): void {
		const input = event.target as HTMLInputElement;
		const value = input.value;
		this.setVariable(variable, value);
	}

	previewVariable(variable: UIThemeVariable, value: string): void {
		variable.value = value;
		this.applyPreviewVariables(this.theme.variables);
	}

	private applyPreviewVariables(variables: UIThemeVariable[]): void {
		this.clearPreviewVariables();

		let style = document.createElement('style');
		style.id = this.PREVIEW_STYLE_ID;
		style.textContent = this.generatePreviewCSS(variables);
		
		document.head.appendChild(style);
	}

	/**
	 * Styling targets :root to override current variables, allowing live preview.
	 */
	private generatePreviewCSS(variables: UIThemeVariable[]): string {
		let lines = variables.map((v: UIThemeVariable) => `  ${v.name}: ${this.quotify(v)};`);
		return `:root {\n${lines.join('\n')}\n}`;
	}

	clearPreviewVariables(): void {
		const el = document.getElementById(this.PREVIEW_STYLE_ID);
		if (el) el.remove();
	}

	private getTheme(): string {
		return this.themeBuilderService.getThemeAttribute();
	}

	private setTheme(themeName: string): void {
		this.themeBuilderService.setThemeAttribute(themeName);
	}
	
	private quotify(v: UIThemeVariable): string {
	  return v.type === 'copy'
	    ? `"${v.value.replace(/"/g, '\\"')}"`
	    : v.value;
	}

	/**
	 * Closes the theme builder.
	 */
	close(): void {
		this.clearPreviewVariables();
		this.themeBuilderService.enableThemes();
		this.setTheme(this.currentThemeName);
		this.themeBuilderService.isOpen = false;
	}
}
